import useFuelingService from '../../../hooks/fueling-services/useFuelingService.ts';
import useUpdateFuelingServiceMetadata from '../../../hooks/fueling-services/useUpdateFuelingServiceMetadata.ts';
import useUpdateFuelingServiceTags from '../../../hooks/fueling-services/useUpdateFuelingServiceTags.ts';
import useEnableFuelingService from '../../../hooks/fueling-services/useEnableFuelingService.ts';
import usePauseFuelingService from '../../../hooks/fueling-services/usePauseFuelingService.ts';
import useDisableFuelingService from '../../../hooks/fueling-services/useDisableFuelingService.ts';
import {
  UpdateFuelingServiceMetadataRequest,
  UpdateFuelingServiceTagsRequest,
} from '../../../api/requests/fueling-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { LoadingOverlay, Modal } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import type { ErrorResponse } from '../../../api/responses/error-response.ts';
import MutateFuelingServiceHeaderInfoForm, {
  MutateFuelingServiceHeaderInfoFormValues,
} from '../../ui/places/MutateFuelingServiceHeaderInfoForm.tsx';

interface UpdateFuelingServiceHeaderInfoModalProps {
  opened: boolean;
  onClose: () => void;
  serviceId: string;
  onSuccess?: () => void;
}

const UpdateFuelingServiceHeaderInfoModal = ({
  opened,
  onClose,
  serviceId,
  onSuccess,
}: UpdateFuelingServiceHeaderInfoModalProps) => {
  const { data: fuelingService, isLoading: isFuelingServiceLoading } =
    useFuelingService(serviceId);

  const { mutateAsync: updateFuelingServiceMetadata } =
    useUpdateFuelingServiceMetadata();
  const { mutateAsync: updateFuelingServiceTags } =
    useUpdateFuelingServiceTags();
  const { mutateAsync: enableFuelingService } = useEnableFuelingService();
  const { mutateAsync: pauseFuelingService } = usePauseFuelingService();
  const { mutateAsync: disableFuelingService } = useDisableFuelingService();

  const {
    mutate: handleSubmit,
    isPending: isUpdateTagsMetadataAndStatusPending,
  } = useMutation<
    void,
    ErrorResponse,
    MutateFuelingServiceHeaderInfoFormValues
  >({
    mutationFn: async (formValues) => {
      const updateFuelingServiceMetadataRequest: UpdateFuelingServiceMetadataRequest =
        {
          airPumpInfo: fuelingService.airPumpInfo,
          brandId: fuelingService.brandId ?? '',
          carMaintenanceInfo: fuelingService.carMaintenanceInfo,
          carWashInfo: fuelingService.carWashInfo,
          convenienceStoreInfo: fuelingService.convenienceStoreInfo,
          hasAccessibleRestroom: fuelingService.hasAccessibleRestroom,
          hasRestroom: fuelingService.hasRestroom,
          name: formValues.name,
          notes: fuelingService.notes,
          ownershipType: fuelingService.ownershipType,
          parkingLotInfo: fuelingService.parkingLotInfo,
          phoneNumber: fuelingService.phoneNumber,
          pricingSource: fuelingService.pricingSource,
          selfServiceTypes: fuelingService.selfServiceTypes,
          serviceId: serviceId,
          supplier: fuelingService.supplier,
          supportedFuelTypes: fuelingService.supportedFuelTypes,
          waterPumpInfo: fuelingService.waterPumpInfo,
          website: fuelingService.website,
        };

      await updateFuelingServiceMetadata(updateFuelingServiceMetadataRequest);

      const updateFuelingServiceTagsRequest: UpdateFuelingServiceTagsRequest = {
        serviceId: serviceId,
        tags: formValues.tags,
      };

      await updateFuelingServiceTags(updateFuelingServiceTagsRequest);

      if (formValues.serviceStatus === 'ONLINE') {
        await enableFuelingService(serviceId);
      } else if (formValues.serviceStatus === 'TEMPORARY_OFFLINE') {
        await pauseFuelingService(serviceId);
      } else {
        await disableFuelingService(serviceId);
      }
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      notifyError('更新服務資訊失敗', error.message);
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="編輯加油資訊"
      centered
      closeOnClickOutside={false}
      size={440}
      styles={{
        title: { fontWeight: 700, fontSize: 16 },
        header: { paddingBottom: 24 },
      }}
    >
      {isFuelingServiceLoading ? (
        <LoadingOverlay visible />
      ) : (
        <MutateFuelingServiceHeaderInfoForm
          initialValues={{
            name: fuelingService.name,
            serviceStatus: fuelingService.serviceStatus,
            tags: fuelingService.tags,
          }}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isSubmitting={isUpdateTagsMetadataAndStatusPending}
        />
      )}
    </Modal>
  );
};

export { UpdateFuelingServiceHeaderInfoModal };
